import React, {
  useMemo,
  useCallback,
  useState,
  useRef,
  useEffect,
} from "react";
import {
  FormControl,
  InputLabel,
  Paper,
  TextField,
  Box,
  Checkbox,
  Button,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Autocomplete from "@mui/material/Autocomplete";
import { useTheme } from "@mui/material/styles";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { shouldBlockUnsafeSearchInput } from "@/helpers/sanitizeHelper";

type SelectOption = {
  label?: string;
  value?: any;
  [key: string]: any;
};

type SelectProps = {
  value?: any;
  onChange?: (_value: any) => void;
  [key: string]: any;
};

type SelectStyles = {
  actionButton: React.CSSProperties;
  applyButton: React.CSSProperties;
  countText: React.CSSProperties;
  [key: string]: React.CSSProperties;
};

type SelectActionsContextValue = {
  multiple: boolean;
  withApplyButton: boolean;
  handleSelectAll: (_event: React.MouseEvent) => void;
  handleClearAll: (_event: React.MouseEvent) => void;
  handleApply: (_event: React.MouseEvent) => void;
  styles: SelectStyles;
  theme: any;
  hasNoOptions: boolean;
};

type RpMuiAutocompleteSelectProps = {
  label?: string;
  name?: string;
  selectProps?: SelectProps;
  labelProps?: Record<string, any>;
  options?: SelectOption[];
  wrapperProps?: Record<string, any>;
  all?: boolean;
  allText?: string;
  isOutlined?: boolean;
  hideLabel?: boolean;
  placeholder?: string;
  multiple?: boolean;
  disableClearable?: boolean;
  renderOption?: ((_option: SelectOption) => React.ReactNode) | null;
  withCheckbox?: boolean;
  withApplyButton?: boolean;
  value?: any;
  onChange?: (_event: React.SyntheticEvent, _value: any) => void;
  disablePortal?: boolean;
  onInputChange?: (..._args: any[]) => void;
  [key: string]: any;
};

const CustomAutocomplete = styled(Autocomplete as any)(({ theme }) => ({
  zIndex: "1 !important",
  borderRadius: "var(--radius-control)",
  border: "1px solid var(--neutral-500)",
  boxSizing: "border-box",
  display: "flex",
  minHeight: "40px",
  transition: theme.transitions.create(["border-color", "box-shadow"]),
  "&:hover": {
    borderRadius: "var(--radius-control)",
    borderColor: "var(--neutral-600) !important",
  },
  // The outline paints above the inner input, so all four sides stay 2px.
  "&:focus-within": {
    borderRadius: "var(--radius-control)",
    borderColor: "var(--primary-base) !important",
    outline: "1px solid var(--primary-base)",
    outlineOffset: "-2px",
  },
  "& .MuiAutocomplete-input": {
    fontSize: ".8rem",
  },
  "& .MuiAutocomplete-popupIndicator": {
    color: "inherit",
  },
  "& .MuiAutocomplete-popper": {
    boxShadow: "var(--shadow-floating)",
  },
  "& .MuiAutocomplete-paper": {
    boxShadow: "none",
  },
  "& .MuiAutocomplete-noOptions": {
    fontSize: ".8rem",
    fontWeight: 600,
    padding: "10px 16px 5px",
  },
  "& .MuiAutocomplete-tag": {
    height: "20px !important",
    fontSize: ".8rem !important",
    fontWeight: "700 !important",
    minWidth: "30px !important",
    margin: "3px !important",
    backgroundColor: `${(theme.vars || theme)?.palette?.primary?.light} !important`,
    color: `${(theme.vars || theme)?.palette?.primary?.main} !important`,
    "& .MuiChip-label": {
      fontSize: ".7rem !important",
      fontWeight: "600 !important",
    },
  },
}));

const StyledPaper = styled(Paper)(() => ({
  backgroundColor: "transparent",
  boxShadow: "none",
}));

const StyledInputLabel = styled(InputLabel)(() => ({
  fontWeight: 600,
  color: "var(--neutral-700)",
  fontSize: ".92rem",
  marginTop: -11,
  marginLeft: -11,
}));

const SelectActionsContext = React.createContext<SelectActionsContextValue>(
  {} as SelectActionsContextValue
);

const AutocompletePaper = (props: any) => {
  const { children, ...other } = props;
  const {
    multiple,
    withApplyButton,
    handleSelectAll,
    handleClearAll,
    handleApply,
    styles,
    theme,
    hasNoOptions,
  } = React.useContext(SelectActionsContext);

  return (
    <Paper {...other}>
      {multiple && (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          px={2}
          py={1}
          borderBottom={`1px solid ${(theme.vars || theme).palette.divider}`}
        >
          <Button
            size="small"
            color="primary"
            onMouseDown={handleSelectAll}
            style={styles.actionButton}
            disabled={hasNoOptions}
          >
            Select All
          </Button>
          <Button
            size="small"
            onMouseDown={handleClearAll}
            style={styles.actionButton}
            disabled={hasNoOptions}
          >
            Clear All
          </Button>
        </Box>
      )}
      {children}
      {multiple && withApplyButton && (
        <Box
          display="flex"
          justifyContent="center"
          px={2}
          py={1.5}
          borderTop={`1px solid ${(theme.vars || theme).palette.divider}`}
          bgcolor={(theme.vars || theme).palette.background.paper}
        >
          <Button
            variant="contained"
            color="primary"
            size="small"
            onMouseDown={handleApply}
            style={styles.applyButton}
            fullWidth
            disabled={hasNoOptions}
          >
            Apply Filters
          </Button>
        </Box>
      )}
    </Paper>
  );
};

function RpMuiAutocompleteSelect({
  label = "",
  name = "select",
  selectProps = {},
  labelProps = {},
  options = [],
  wrapperProps = {},
  all = false,
  allText = "All",
  isOutlined = false,
  hideLabel = false,
  placeholder = "Search..",
  multiple = false,
  disableClearable = true,
  renderOption = null, // Custom render function for options
  withCheckbox = false,
  withApplyButton = false,
  value,
  onChange,
  disablePortal = false,
  ...rest
}: RpMuiAutocompleteSelectProps) {
  const { onInputChange: externalOnInputChange, ...autocompleteProps } = rest;
  const [inputValue, setInputValue] = useState("");
  const [open, setOpen] = useState(false);
  const [stagedValue, setStagedValue] = useState<any[]>([]);
  const theme = useTheme();

  // Use a ref to keep handlers stable and avoid content refresh loops
  const stagedRef = useRef(stagedValue);
  useEffect(() => {
    stagedRef.current = stagedValue;
  }, [stagedValue]);

  const localValue = value !== undefined ? value : selectProps.value;
  const localValueRef = useRef(localValue);
  useEffect(() => {
    localValueRef.current = localValue;
  }, [localValue]);

  const dropdownOptions = useMemo(
    () => (all ? [{ label: allText, value: "" }, ...options] : options),
    [options, all, allText]
  );

  const currentSelection = useMemo(() => {
    if (withApplyButton && multiple && open) {
      return stagedValue;
    }
    return localValue;
  }, [withApplyButton, multiple, open, stagedValue, localValue]);

  const selectedValue = useMemo(() => {
    if (multiple) {
      const selection = Array.isArray(currentSelection) ? currentSelection : [];
      return selection.map(
        (val: any) =>
          dropdownOptions.find((o) => o.value === val) || {
            label: val,
            value: val,
          }
      );
    } else {
      const val = currentSelection;
      const v = dropdownOptions.find((o) => o.value === val);
      return v || (all ? { label: allText, value: "" } : null);
    }
  }, [currentSelection, dropdownOptions, all, allText, multiple]);

  // Sync inputValue with selectedValue to handle external resets
  useEffect(() => {
    if (multiple) {
      setInputValue("");
    } else {
      setInputValue(
        Array.isArray(selectedValue) ? "" : selectedValue?.label || ""
      );
    }
  }, [selectedValue, multiple, localValue]);

  const handleChange = useCallback(
    (event: React.SyntheticEvent, newValue: any) => {
      let values;
      if (multiple) {
        values = (Array.isArray(newValue) ? newValue : []).map(
          (item: SelectOption) => item.value
        );

        // Handle "All" option special case
        const hasAll = values.includes("");
        const wasAllSelected = (
          Array.isArray(currentSelection) ? currentSelection : []
        ).includes("");

        if (hasAll && !wasAllSelected) {
          values = dropdownOptions.map((o) => o.value);
        } else if (!hasAll && wasAllSelected) {
          values = [];
        } else if (hasAll && values.length < dropdownOptions.length) {
          values = values.filter((v: any) => v !== "");
        } else if (
          !hasAll &&
          all &&
          values.length === dropdownOptions.length - 1 &&
          dropdownOptions.length > 0
        ) {
          values = ["", ...values];
        }
      } else {
        values = newValue?.value;
      }

      if (withApplyButton && multiple) {
        setStagedValue(values);
      } else {
        if (onChange) {
          onChange(event, values);
        } else {
          selectProps.onChange?.(values);
        }
      }
    },
    [
      onChange,
      selectProps,
      multiple,
      dropdownOptions,
      all,
      currentSelection,
      withApplyButton,
    ]
  );

  const displayedAppliedValue = useMemo(() => {
    const selection = Array.isArray(localValue) ? localValue : [];
    return selection.map(
      (val: any) =>
        dropdownOptions.find((o) => o.value === val) || {
          label: val,
          value: val,
        }
    );
  }, [localValue, dropdownOptions]);

  const handleApply = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      const valueToApply = stagedRef.current;
      if (onChange) {
        onChange(event, valueToApply);
      } else {
        selectProps.onChange?.(valueToApply);
      }
      setOpen(false);
    },
    [onChange, selectProps]
  );

  const handleClearAll = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (withApplyButton && multiple) {
        setStagedValue([]);
      } else {
        const resetValue = multiple ? [] : all ? "" : undefined;
        if (onChange) {
          onChange(event, resetValue);
        } else {
          selectProps.onChange?.(resetValue);
        }
      }
    },
    [onChange, selectProps, withApplyButton, multiple, all]
  );

  const handleSelectAll = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      const allValues = dropdownOptions.map((o) => o.value);

      if (withApplyButton && multiple) {
        setStagedValue(allValues);
      } else {
        if (onChange) {
          onChange(event, allValues);
        } else {
          selectProps.onChange?.(allValues);
        }
      }
    },
    [onChange, selectProps, dropdownOptions, withApplyButton, multiple]
  );

  const handleOpen = () => {
    if (withApplyButton && multiple) {
      setStagedValue(localValueRef.current || []);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleInputChange = useCallback(
    (event: React.SyntheticEvent, newInputValue: string, reason: string) => {
      if (reason === "input" && shouldBlockUnsafeSearchInput(newInputValue)) {
        return;
      }

      setInputValue(newInputValue);
      externalOnInputChange?.(event, newInputValue, reason);
    },
    [externalOnInputChange]
  );

  const getStyles = useCallback(
    () => ({
      autocomplete: {
        background: rest.disabled
          ? (theme.vars || theme)?.palette?.neutral?.[200] ||
            "var(--neutral-200)"
          : isOutlined
            ? "transparent"
            : (theme.vars || theme)?.palette?.background?.paper,
        color: rest.disabled
          ? (theme.vars || theme)?.palette?.neutral?.[500] ||
            "var(--neutral-500)"
          : isOutlined
            ? (theme.vars || theme)?.palette?.primary?.main
            : (theme.vars || theme)?.palette?.text?.primary,
        borderRadius: "var(--radius-control)",
        border: `1px solid ${
          rest.disabled
            ? (theme.vars || theme)?.palette?.action?.disabledBackground
            : "var(--neutral-500)"
        }`,
      },
      input: {
        alignItems: "center",
        color: "inherit",
        border: "none",
        fontSize: ".8rem",
        minHeight: "38px",
        padding: "0 4px",
      },
      popupIndicator: {
        color: rest.disabled
          ? (theme.vars || theme)?.palette?.neutral?.[500] ||
            "var(--neutral-500)"
          : isOutlined
            ? (theme.vars || theme)?.palette?.primary?.main
            : (theme.vars || theme)?.palette?.text?.secondary,
      },
      actionButton: {
        fontSize: "0.7rem",
        fontWeight: 600,
        textTransform: "none",
        padding: "2px 8px",
      },
      applyButton: {
        fontSize: "0.8rem",
        fontWeight: 600,
        textTransform: "none",
        padding: "8px 16px",
        boxShadow: "none",
      },
      countText: {
        fontSize: "0.75rem",
        fontWeight: 700,
        marginLeft: "4px",
        whiteSpace: "nowrap",
        color: "inherit",
      },
    }),
    [rest.disabled, isOutlined, theme]
  );

  const tagSx = useMemo(
    () => ({
      m: "2px !important",
      flexShrink: 0,
      maxWidth: "120px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      // Selected-value badge: primary tint surface with primary base text.
      backgroundColor: `${(theme.vars || theme).palette.primary.light} !important`,
      color: `${(theme.vars || theme).palette.primary.main} !important`,
      display: "flex",
      alignItems: "center",
      borderRadius: "var(--radius-control)",
    }),
    [theme]
  );

  const styles = getStyles();

  const hasNoOptions = useMemo(() => {
    return dropdownOptions.length === 0;
  }, [dropdownOptions]);

  const paperContextValue = useMemo(
    () => ({
      multiple,
      withApplyButton,
      handleSelectAll,
      handleClearAll,
      handleApply,
      styles,
      theme,
      hasNoOptions,
    }),
    [
      multiple,
      withApplyButton,
      handleSelectAll,
      handleClearAll,
      handleApply,
      styles,
      theme,
      hasNoOptions,
    ]
  );

  return (
    <SelectActionsContext.Provider value={paperContextValue}>
      <StyledPaper elevation={0} {...wrapperProps}>
        <FormControl fullWidth margin="dense" className="my-0">
          {!hideLabel && (
            <StyledInputLabel
              shrink
              id={`${name}-label`}
              {...labelProps}
              disableAnimation
              required={rest?.required}
              sx={{
                ...labelProps?.sx,
                color: (theme.vars || theme).palette.text.primary,
              }}
            >
              {label}
            </StyledInputLabel>
          )}
          <CustomAutocomplete
            {...autocompleteProps}
            disablePortal={disablePortal}
            style={styles.autocomplete}
            disableClearable={disableClearable}
            clearIcon={<HighlightOffIcon style={styles.popupIndicator} />}
            openOnFocus
            autoHighlight // Fix focus on search
            disableCloseOnSelect={multiple} // Keep popup open for multi-select
            id={name}
            name={name}
            fullWidth
            multiple={multiple}
            value={selectedValue}
            onChange={handleChange}
            open={open}
            onOpen={handleOpen}
            onClose={handleClose}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            options={dropdownOptions}
            getOptionLabel={(option: SelectOption) => option?.label || ""}
            isOptionEqualToValue={(option: SelectOption, value: SelectOption) =>
              option?.value === value?.value
            }
            PaperComponent={AutocompletePaper}
            renderTags={(tagValue: SelectOption[], getTagProps: any) => {
              const tagsToShow =
                withApplyButton && multiple && open
                  ? displayedAppliedValue
                  : tagValue;

              if (!multiple || tagsToShow.length === 0) return null;

              // Filter out "All" if it was automatically added and others are selected
              const effectiveValue =
                all && tagsToShow.length > 1
                  ? tagsToShow.filter((v: SelectOption) => v.value !== "")
                  : tagsToShow;

              if (effectiveValue.length === 0) return null;

              return (
                <Box
                  display="flex"
                  alignItems="center"
                  flexWrap="nowrap"
                  sx={{ overflow: "hidden" }}
                >
                  <Box
                    {...getTagProps({ index: 0 })}
                    className="MuiAutocomplete-tag"
                    component="div"
                    sx={tagSx}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        px: 1,
                        color: "inherit",
                      }}
                    >
                      {effectiveValue[0].label}
                    </Box>
                  </Box>
                  {effectiveValue.length > 1 && (
                    <Box sx={styles.countText}>
                      +{effectiveValue.length - 1} selected
                    </Box>
                  )}
                </Box>
              );
            }}
            slotProps={{
              popper: {
                style: {
                  zIndex: 9999,
                },
                sx: {
                  marginTop: "5px !important",
                },
              },
            }}
            renderOption={(props: any, option: SelectOption, { selected }) => {
              // Use custom render function if provided
              if (renderOption) {
                return (
                  <li {...props}>
                    {multiple && withCheckbox && (
                      <Checkbox
                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                    )}
                    {renderOption(option)}
                  </li>
                );
              }

              // Default rendering
              return (
                <li {...props}>
                  {multiple && withCheckbox && (
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                  )}
                  <Box className="d-flex align-items-center">
                    <span>{option?.label || ""}</span>
                  </Box>
                </li>
              );
            }}
            ListboxProps={{
              sx: {
                "& .MuiAutocomplete-option": {
                  fontSize: ".8rem",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    fontWeight: 600,
                    backgroundColor: (theme.vars || theme).palette.action.hover,
                  },
                  "&.Mui-focused": {
                    backgroundColor: (theme.vars || theme).palette.action.hover,
                  },
                  "&.Mui-selected": {
                    backgroundColor: (theme.vars || theme).palette.action
                      .selected,
                    color: (theme.vars || theme)?.palette?.primary?.main,
                    fontWeight: 600,
                  },
                },
              },
            }}
            renderInput={(params: any) => {
              const isStaging = withApplyButton && multiple && open;
              const hasAppliedValue = displayedAppliedValue.length > 0;
              const inputProps = {
                ...params.InputProps,
                disableUnderline: true, // Fix visible line
                style: styles.input, // Restore input styles
              };

              // If we are staging, force the startAdornment to show the APPLIED state
              if (isStaging) {
                inputProps.startAdornment = (
                  <Box
                    display="flex"
                    alignItems="center"
                    flexWrap="nowrap"
                    sx={{ overflow: "hidden" }}
                  >
                    {hasAppliedValue && (
                      <>
                        <Box
                          className="MuiAutocomplete-tag"
                          component="div"
                          sx={tagSx}
                        >
                          <Box
                            component="span"
                            sx={{
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              px: 1,
                              color: "inherit",
                            }}
                          >
                            {displayedAppliedValue[0].label}
                          </Box>
                        </Box>
                        {displayedAppliedValue.length > 1 && (
                          <Box sx={styles.countText}>
                            +{displayedAppliedValue.length - 1} selected
                          </Box>
                        )}
                      </>
                    )}
                  </Box>
                );
              }

              return (
                <TextField
                  {...params}
                  variant="standard"
                  placeholder={
                    isStaging
                      ? hasAppliedValue
                        ? ""
                        : placeholder
                      : multiple
                        ? selectedValue?.length > 0
                          ? ""
                          : placeholder
                        : !!selectedValue
                          ? ""
                          : placeholder
                  }
                  InputProps={inputProps}
                  sx={{
                    "& .MuiInputBase-root": {
                      padding: "0 0 0 10px !important",
                      minHeight: "38px",
                      display: "flex",
                      alignItems: "center",
                      "&::before, &::after": {
                        display: "none !important", // Absolute fix for the line
                      },
                    },
                  }}
                />
              );
            }}
            popupIcon={<ArrowDropDownIcon style={styles.popupIndicator} />}
          />
        </FormControl>
      </StyledPaper>
    </SelectActionsContext.Provider>
  );
}

export default RpMuiAutocompleteSelect;
